import { inject, Injectable } from '@angular/core';
import { lastValueFrom, Observable, tap } from 'rxjs';
import { AlphaHttpClient } from './alpha-http-client';
import { TenantService } from './tenant.service';

let PROJECT_SERVICE_INSTANCES = 0;

export interface ProjectTag {
    id: number;
    name: string;
    comment: string;
    currentTag: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProjectService {
    private httpClient = inject(AlphaHttpClient);
    private tenantService = inject(TenantService);
    private _project: IProject = {} as IProject;
    private domainUrl = {
        type: 'environment',
        value: {
            __alpha_env__: 'ALPHA_UI_CONFIG_SERVICE_URL',
        },
    };
    private cachedProjectTags: ProjectTag[] = [];
    private cachedProjectId: string = '';

    constructor() {
        PROJECT_SERVICE_INSTANCES++;
        if (PROJECT_SERVICE_INSTANCES > 1) {
            console.error(new Error('Cannot instantiate more than one ProjectService'));
            alert('FATAL: Project service is being instantiated more than once. Please report to the Product Team.');
        }
    }

    getAllProjects(
        pageSize: number = 10,
        pageNumber: number = 0,
    ): Observable<{ projects: IProject[]; totalCount: number }> {
        return this.httpClient.post(this.domainUrl, '/project/fetch-all', {
            tenant: this.tenantService.fetchTenant(),
            pageSize,
            pageNumber,
        });
    }

    projectNameCheck(projectName: string) {
        return this.httpClient.post(this.domainUrl, '/project/check-project-name', {
            tenant: this.tenantService.fetchTenant(),
            projectName,
        });
    }

    searchProject(searchKey?: string, status?: any[], skip?: number, limit?: number, sortby?: any) {
        return this.httpClient.post(this.domainUrl, '/project/search-project', {
            tenant: this.tenantService.fetchTenant(),
            searchKey,
            status,
            skip,
            limit,
            sortby,
        });
    }

    createProject(project: IProject) {
        return this.httpClient.post(this.domainUrl, '/project/save', {
            tenant: this.tenantService.fetchTenant(),
            ...project,
        });
    }

    removeProject(projectId: string) {
        return this.httpClient.post(this.domainUrl, '/project/remove', {
            tenant: this.tenantService.fetchTenant(),
            projectId,
        });
    }

    setCurrentProject(project: IProject) {
        this._project = project;
    }

    getProject(projectId: any): Observable<IProject> {
        return this.httpClient.post(this.domainUrl, '/project/fetch', {
            projectId,
        });
    }

    fetchCurrentProject(): IProject {
        return this._project || ({} as IProject);
    }

    async setProject(projectId: string | null): Promise<IProject> {
        if (!projectId) {
            throw new Error('Project Id Not Found');
        }
        const project = await lastValueFrom(this.getProject(projectId));
        if (project) {
            this.setCurrentProject(project);
            return project;
        } else {
            throw new Error('Project Not Found');
        }
    }

    importProject(file: File) {
        const formData = new FormData();
        formData.append('file', file, file.name);
        return this.httpClient.post(this.domainUrl, '/project/import', formData, {
            isFile: true,
        });
    }

    publishProject(projectId: string, comment: string) {
        return this.httpClient.post(this.domainUrl, '/project/tag/add', { projectId, comment });
    }

    async getProjectTags(projectId: string, forceRefresh: boolean = false): Promise<ProjectTag[]> {
        if (
            !this.cachedProjectTags ||
            this.cachedProjectTags.length === 0 ||
            forceRefresh ||
            this.cachedProjectId !== projectId
        ) {
            this.cachedProjectId = projectId;
            this.cachedProjectTags = await lastValueFrom(
                this.httpClient.post(this.domainUrl, '/project/tag/fetch-all', { projectId }),
            );
        }
        return this.cachedProjectTags;
    }

    async getLatestProjectTag(projectId: string, forceRefresh: boolean = false) {
        const tags = (await this.getProjectTags(projectId, forceRefresh)) || [];
        return tags[0];
    }

    setCurrentTag(projectId: string, tag: string) {
        return this.httpClient.post(this.domainUrl, '/project/tag/current', { projectId, tag });
    }

    exportProjectAsync(projectId: string, tag: string) {
        return this.httpClient.post(this.domainUrl, '/project-export', { projectId, tag });
    }

    updateAppConfig(projectId: string, appConfig: Record<string, any>): Observable<IProject> {
        return this.httpClient
            .post<IProject>(this.domainUrl, '/project/update/app-config', {
                projectId,
                appConfig,
            })
            .pipe(
                tap((project: IProject) => {
                    Object.assign(this._project, project);
                }),
            );
    }
}

export interface IProject {
    id: number;
    projectId: string;
    name: string;
    caseType: string;
    icon: string;
    version: string;
    createdAt: string; // ISO 8601 Date string
    updatedAt: string; // ISO 8601 Date string
    status: string;
    disable: boolean;
    description: string;
    active: boolean;
    enquiry: boolean;
    appConfig: Record<string, any>;
}
